import { memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { thousandFormatNumbroCurrency } from '@fintronners/react-utils/src/numberUtilsTSX';
import { WebBodyText14, WebCaption10 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import { useTranslation } from 'react-i18next';

import { useIsIRAAccount } from '@/hooks/utils/useCurrentAccountType';
import { Skeleton } from '@/components/common/Skeleton';
import { InfoWithIcon } from '../InfoWithIcon';
import { TransferCurrencyInput } from '../TransferCurrencyInput';
import { OneTimeDepositPreselectedOptions } from './OneTimeDepositPreselectedOptions';
import { ContributionsSelectWidget } from '@/components/common/ContributionSelectWidget/ContributionSelectWidget';

export type OneTimeDepositFormProps = {
  availableDailyDepositLimit: string;
  isLoading: boolean;
  handleToggleFormState: () => void;
};

const OneTimeDepositForm = memo(
  ({ availableDailyDepositLimit, isLoading, handleToggleFormState }: OneTimeDepositFormProps) => {
    const { control } = useFormContext();
    const { t } = useTranslation();
    const isIRAAccount = useIsIRAAccount();

    return (
      <>
        <TransferCurrencyInput />
        <OneTimeDepositPreselectedOptions />
        {isLoading && <Skeleton className="h-4 w-full" />}
        {!isLoading && !isIRAAccount && (
          <>
            <InfoWithIcon
              message={t('miscScreens.deposits.instantDepositLimit', {
                limit: thousandFormatNumbroCurrency(availableDailyDepositLimit),
              })}
            />
            <WebCaption10.Regular className="text-grey55">
              {t('miscScreens.deposits.instantDepositTimeWindow')}
            </WebCaption10.Regular>
          </>
        )}
        {!isLoading && isIRAAccount && (
          <>
            <WebBodyText14.Regular className="text-grey72">
              {t('miscScreens.deposits.contributionSelectDescription')}
            </WebBodyText14.Regular>
            <Controller
              name="taxYear"
              control={control}
              render={({ field }) => (
                <ContributionsSelectWidget
                  {...field}
                  disabled={
                    false
                    /**
                     * Forcing this field to be enabled since the user should be able
                     * to switch Tax Years even when the rest of the form is disabled
                     */
                  }
                  setSelectedTaxYear={field.onChange}
                  handleToggleFormState={handleToggleFormState}
                />
              )}
            />
          </>
        )}
      </>
    );
  },
);

export default OneTimeDepositForm;
